import type { FC } from "react";

import { NavRow } from "@/components/ui/nav-row";
import { paneTitleClass } from "@/components/ui/pane-header";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";

import {
  attentionCount,
  NOTICE_WORKFLOWS,
  noticeBadge,
  useKeyFacts,
  useNoticeBrief
} from "../../keyFacts";
import { useCaseIde } from "../context/CaseIdeContext";
import { artifactKey } from "../types";

/**
 * The explorer's Key Facts row: opens the Key Facts tab and shows what needs
 * the attorney (live with the tab).
 */
export const KeyFactsHeader: FC<{ individualId: string }> = ({
  individualId
}) => {
  const { activeKey, open } = useCaseIde();
  const { data } = useKeyFacts(individualId);
  const noticeCase = NOTICE_WORKFLOWS.has(data?.workflow);
  const { data: notice } = useNoticeBrief(individualId, noticeCase);
  const issues = noticeCase ? noticeBadge(notice?.brief) : null;
  const count = data ? attentionCount(data.facts, data.workflow) : 0;
  const updating = data?.settings.reconcile_state === "running";
  const active =
    activeKey === artifactKey({ kind: "key_facts", id: individualId });

  return (
    <NavRow
      data-testid="key-facts-header"
      active={active}
      onClick={() =>
        open({ kind: "key_facts", id: individualId, title: "Case Insights" })
      }
      trailing={
        <span className="flex items-center gap-1.5">
          {updating ? <Spinner className="size-3 text-fg-3" /> : null}
          {issues ? (
            <StatusBadge
              tone={issues.tone}
              className="tabular-nums"
              aria-label={issues.label}
            >
              {issues.text}
            </StatusBadge>
          ) : count > 0 ? (
            <StatusBadge
              tone="warn"
              className="tabular-nums"
              aria-label={`${count} need${
                count === 1 ? "s" : ""
              } your attention`}
            >
              {count} to Review
            </StatusBadge>
          ) : null}
        </span>
      }
    >
      <span className={paneTitleClass}>Case Insights</span>
    </NavRow>
  );
};
